// BC statutory holidays (CR #200) — the 11 stats observed in British Columbia.
// Pure date math, works for any year. Note: Easter Monday and Boxing Day are
// NOT statutory holidays in BC and are deliberately excluded.

export interface BCStatHoliday {
  /** Local calendar date, YYYY-MM-DD */
  date: string;
  name: string;
}

const ymd = (year: number, month: number, day: number) =>
  `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

/** Easter Sunday via the Anonymous Gregorian (Gauss-style) algorithm. */
function easterSunday(year: number): Date {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31); // 3=March, 4=April
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

/** Nth Monday (1-based) of a month. `month` is 1–12. */
function nthMonday(year: number, month: number, n: number): number {
  const firstDow = new Date(year, month - 1, 1).getDay(); // 0=Sun
  const firstMonday = firstDow <= 1 ? 2 - firstDow : 9 - firstDow;
  return firstMonday + (n - 1) * 7;
}

/** Victoria Day — the last Monday before May 25. */
function victoriaDay(year: number): number {
  const may24Dow = new Date(year, 4, 24).getDay();
  // Walk back from May 24 to a Monday (May 24 itself qualifies when it's a Monday).
  return 24 - ((may24Dow + 6) % 7);
}

/** All BC statutory holidays for a year, sorted by date. */
export function bcStatHolidays(year: number): BCStatHoliday[] {
  const easter = easterSunday(year);
  const goodFriday = new Date(year, easter.getMonth(), easter.getDate() - 2);
  return [
    { date: ymd(year, 1, 1), name: "New Year's Day" },
    { date: ymd(year, 2, nthMonday(year, 2, 3)), name: 'Family Day' },
    { date: ymd(year, goodFriday.getMonth() + 1, goodFriday.getDate()), name: 'Good Friday' },
    { date: ymd(year, 5, victoriaDay(year)), name: 'Victoria Day' },
    { date: ymd(year, 7, 1), name: 'Canada Day' },
    { date: ymd(year, 8, nthMonday(year, 8, 1)), name: 'BC Day' },
    { date: ymd(year, 9, nthMonday(year, 9, 1)), name: 'Labour Day' },
    { date: ymd(year, 9, 30), name: 'National Day for Truth & Reconciliation' },
    { date: ymd(year, 10, nthMonday(year, 10, 2)), name: 'Thanksgiving' },
    { date: ymd(year, 11, 11), name: 'Remembrance Day' },
    { date: ymd(year, 12, 25), name: 'Christmas Day' },
  ].sort((a, b) => a.date.localeCompare(b.date));
}
